import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AddCircleOutline,
  Cancel as CancelIcon,
  CheckCircle,
  CloudUpload,
  Delete as DeleteIcon,
  InfoOutlined,
  RestartAlt,
} from '@mui/icons-material';
import { addLocalSupplierItem, fetchCategories } from '../../services/itemService';
import { deleteUploadedImage, uploadManualCartImage } from '../../services/manualCart';

const createRandomId = () => Math.floor(100000 + Math.random() * 900000).toString();
const createImageId = () => `${Date.now()}-${Math.random()}`;
const maxFileSize = 10 * 1024 * 1024;
const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const createEmptySpec = () => ({
  id: `${Date.now()}-${Math.random()}`,
  name: '',
  value: '',
  imageUrl: '',
  price: '',
  quantity: '',
  confirmed: false,
  skuId: null,
  variants: [],
});

const createEmptyVariant = () => ({
  id: `${Date.now()}-${Math.random()}`,
  name: '',
  value: '',
  imageUrl: '',
  price: '',
  quantity: '',
  confirmed: false,
  skuId: null,
});

const initialFormState = {
  title: '',
  description: '',
  categoryId: '',
  price: '',
  moq: '1',
  detailUrl: '',
  supplierName: '',
  supplierPhone: '',
  supplierWechat: '',
  supplierNote: '',
  stock: '',
  weight: '',
  volume: '',
};

const LocalSupplierItemsPage = () => {
  const { t } = useTranslation('localSupplierItems');
  const fileInputRef = useRef(null);
  const [formState, setFormState] = useState(initialFormState);
  const [imageState, setImageState] = useState({
    files: [],
    uploading: false,
    dragActive: false,
  });
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryError, setCategoryError] = useState('');
  const [errors, setErrors] = useState({});
  const [specs, setSpecs] = useState([]);
  const [skuMap, setSkuMap] = useState({});
  const [previewPayload, setPreviewPayload] = useState(null);
  const [serverResponse, setServerResponse] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      setLoadingCategories(true);
      setCategoryError('');
      try {
        const data = await fetchCategories({ fetchAll: true });
        if (mounted) {
          setCategories(data);
        }
      } catch (error) {
        if (mounted) {
          setCategoryError(
            error?.response?.data?.detail ||
              error?.message ||
              t('loadCategoriesFailed')
          );
        }
      } finally {
        if (mounted) {
          setLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, [t]);

  useEffect(() => () => {
    imageState.files.forEach((fileEntry) => {
      URL.revokeObjectURL(fileEntry.preview);
    });
  }, [imageState.files]);

  const resetForm = useCallback(() => {
    imageState.files.forEach((fileEntry) => {
      URL.revokeObjectURL(fileEntry.preview);
    });
    setFormState(initialFormState);
    setImageState({ files: [], uploading: false, dragActive: false });
    setErrors({});
    setSpecs([]);
    setSkuMap({});
    setPreviewPayload(null);
    setServerResponse(null);
    setSnackbar({
      open: true,
      severity: 'info',
        message: t('formResetSuccess'),
    });
  }, [imageState.files, t]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleDrag = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setImageState((prev) => ({ ...prev, dragActive: true }));
    } else if (event.type === 'dragleave') {
      setImageState((prev) => ({ ...prev, dragActive: false }));
    }
  }, []);

  const handleFileSelect = useCallback(async (files) => {
    const uploadedFiles = [];
    const selectedFiles = Array.from(files || []).filter((file) => {
      if (!acceptedFileTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          images: t('validation.invalidFileType', { fileName: file.name }),
        }));
        setSnackbar({
          open: true,
          severity: 'error',
          message: t('validation.invalidFileType', { fileName: file.name }),
        });
        return false;
      }
      if (file.size > maxFileSize) {
        setErrors((prev) => ({
          ...prev,
          images: t('validation.fileTooLarge', {
            fileName: file.name,
            maxSize: Math.round(maxFileSize / (1024 * 1024)),
          }),
        }));
        setSnackbar({
          open: true,
          severity: 'error',
          message: t('validation.fileTooLarge', {
            fileName: file.name,
            maxSize: Math.round(maxFileSize / (1024 * 1024)),
          }),
        });
        return false;
      }
      return true;
    });

    if (!selectedFiles.length) {
      return;
    }

    const newFiles = selectedFiles.map((file) => ({
      id: createImageId(),
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      fileId: null,
      uploadedUrl: '',
      deleting: false,
    }));

    setErrors((prev) => ({
      ...prev,
      images: '',
    }));
    setImageState((prev) => ({
      ...prev,
      files: [...prev.files, ...newFiles],
      uploading: true,
      dragActive: false,
    }));

    try {
      for (const fileEntry of newFiles) {
        const response = await uploadManualCartImage(fileEntry.file);
        const uploadedUrl = response?.file_url || response?.url || response?.uri;
        if (!uploadedUrl) {
          throw new Error(t('uploadMissingUrl'));
        }
        uploadedFiles.push({
          ...fileEntry,
          fileId: response?.file_id || null,
          uploadedUrl,
        });
      }

      const uploadedMap = new Map(uploadedFiles.map((fileEntry) => [fileEntry.id, fileEntry]));
      setImageState((prev) => ({
        ...prev,
        files: prev.files.map((fileEntry) =>
          uploadedMap.has(fileEntry.id)
            ? { ...fileEntry, ...uploadedMap.get(fileEntry.id) }
            : fileEntry
        ),
        uploading: false,
      }));
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('imagesUploadedSuccess', { count: uploadedFiles.length }),
      });
    } catch (error) {
      await Promise.all(
        uploadedFiles
          .filter((fileEntry) => fileEntry.fileId)
          .map((fileEntry) => deleteUploadedImage(fileEntry.fileId).catch(() => null))
      );
      const failedFileIds = new Set(newFiles.map((fileEntry) => fileEntry.id));
      newFiles.forEach((fileEntry) => {
        URL.revokeObjectURL(fileEntry.preview);
      });
      setImageState((prev) => ({
        ...prev,
        files: prev.files.filter((fileEntry) => !failedFileIds.has(fileEntry.id)),
        uploading: false,
      }));
      const message =
        error?.response?.data?.error ||
        error?.message ||
        t('failedToUploadImage');
      setErrors((prev) => ({
        ...prev,
        images: message,
      }));
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    }
  }, [t]);

  const handleRemoveImage = useCallback(async (imageId) => {
    const target = imageState.files.find((fileEntry) => fileEntry.id === imageId);
    if (!target) {
      return;
    }

    setImageState((prev) => ({
      ...prev,
      files: prev.files.map((fileEntry) =>
        fileEntry.id === imageId
          ? { ...fileEntry, deleting: true }
          : fileEntry
      ),
    }));

    try {
      if (target.fileId) {
        await deleteUploadedImage(target.fileId);
      }

      if (target.preview) {
        URL.revokeObjectURL(target.preview);
      }

      setImageState((prev) => ({
        ...prev,
        files: prev.files.filter((fileEntry) => fileEntry.id !== imageId),
      }));
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('messages.imageDeleted'),
      });
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        t('messages.imageDeleteFailed');
      setImageState((prev) => ({
        ...prev,
        files: prev.files.map((fileEntry) =>
          fileEntry.id === imageId
            ? { ...fileEntry, deleting: false }
            : fileEntry
        ),
      }));
      setErrors((prev) => ({
        ...prev,
        images: message,
      }));
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    }
  }, [imageState.files, t]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setImageState((prev) => ({ ...prev, dragActive: false }));
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileSelect(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  }, [handleFileSelect]);

  const handleImageChange = useCallback((event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileSelect(event.target.files);
    }
    event.target.value = '';
  }, [handleFileSelect]);

  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);

  const removeSkuEntry = useCallback((skuId) => {
    if (!skuId) {
      return;
    }
    setSkuMap((prev) => {
      if (!prev[skuId]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[skuId];
      return updated;
    });
  }, []);

  const handleAddSpec = () => {
    setSpecs((prev) => [...prev, createEmptySpec()]);
  };

  const handleRemoveSpec = (specId) => {
    setSpecs((prev) => {
      const target = prev.find((spec) => spec.id === specId);
      if (target) {
        if (target.skuId) {
          removeSkuEntry(target.skuId);
        }
        target.variants.forEach((variant) => {
          if (variant.skuId) {
            removeSkuEntry(variant.skuId);
          }
        });
      }
      return prev.filter((spec) => spec.id !== specId);
    });
  };

  const updateSpecField = (specId, field, value) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        if (field !== 'variants' && spec.skuId) {
          removeSkuEntry(spec.skuId);
        }
        return {
          ...spec,
          [field]: value,
          confirmed: false,
          skuId: field === 'variants' ? spec.skuId : null,
        };
      })
    );
  };

  const updateVariantField = (specId, variantId, field, value) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        const variants = spec.variants.map((variant) => {
          if (variant.id !== variantId) {
            return variant;
          }
          if (variant.skuId) {
            removeSkuEntry(variant.skuId);
          }
          return {
            ...variant,
            [field]: value,
            confirmed: false,
            skuId: null,
          };
        });
        return { ...spec, variants };
      })
    );
  };

  const handleAddVariant = (specId) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        return {
          ...spec,
          variants: [...spec.variants, createEmptyVariant()],
        };
      })
    );
  };

  const handleRemoveVariant = (specId, variantId) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        const targetVariant = spec.variants.find((variant) => variant.id === variantId);
        if (targetVariant?.skuId) {
          removeSkuEntry(targetVariant.skuId);
        }
        return {
          ...spec,
          variants: spec.variants.filter((variant) => variant.id !== variantId),
        };
      })
    );
  };

  const buildSkuPayload = ({
    specIndex,
    variantIndex,
    specName,
    specValue,
    variantName,
    variantValue,
    imageUrl,
    price,
    quantity,
  }) => {
    const parsedPrice = Number(price);
    const parsedQty = Number(quantity);
    const skuId = createRandomId();

    const specPart = `0:${specIndex}`;
    const specNamePart = `${specPart}:${specName}:${specValue}`;

    const properties = variantName ? `${specPart};1:${variantIndex}` : specPart;
    const propertiesName = variantName
      ? `${specNamePart};1:${variantIndex}:${variantName}:${variantValue}`
      : specNamePart;

    return {
      skuId,
      payload: {
        properties,
        properties_name: propertiesName,
        name: variantName ? `${specName} / ${variantName}` : specName,
        value: variantName ? `${specValue} / ${variantValue}` : specValue,
        image_url: imageUrl || '',
        price: Number.isFinite(parsedPrice) ? parsedPrice : 0,
        quantity: Number.isFinite(parsedQty) ? parsedQty : 0,
      },
    };
  };

  const validateSpecFields = (spec, includePricing = true) => {
    if (!spec.name?.trim() || !spec.value?.trim()) {
      return t('validation.specNameValueRequired');
    }
    if (includePricing) {
      if (!spec.price || Number(spec.price) <= 0) {
        return t('validation.specPricePositive');
      }
      if (spec.quantity === '' || Number(spec.quantity) < 0) {
        return t('validation.specQuantityNonNegative');
      }
    }
    return '';
  };

  const validateVariantFields = (variant) => {
    if (!variant.name?.trim() || !variant.value?.trim()) {
      return t('validation.variantNameValueRequired');
    }
    if (!variant.price || Number(variant.price) <= 0) {
      return t('validation.variantPricePositive');
    }
    if (variant.quantity === '' || Number(variant.quantity) < 0) {
      return t('validation.variantQuantityNonNegative');
    }
    return '';
  };

  const handleGenerateSpecSku = (specId) => {
    const specIndex = specs.findIndex((spec) => spec.id === specId);
    if (specIndex === -1) return;

    const spec = specs[specIndex];
    const errorMessage = validateSpecFields(spec, true);
    if (errorMessage) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: errorMessage,
      });
      return;
    }

    const { skuId, payload } = buildSkuPayload({
      specIndex,
      specName: spec.name.trim(),
      specValue: spec.value.trim(),
      imageUrl: spec.imageUrl?.trim() || '',
      price: spec.price,
      quantity: spec.quantity,
    });

    setSkuMap((prev) => ({
      ...prev,
      [skuId]: payload,
    }));

    setSpecs((prev) =>
      prev.map((entry) =>
        entry.id === specId
          ? {
              ...entry,
              confirmed: true,
              skuId,
            }
          : entry
      )
    );

    setSnackbar({
      open: true,
      severity: 'success',
      message: t('messages.specConverted'),
    });
  };

  const handleGenerateVariantSku = (specId, variantId) => {
    const specIndex = specs.findIndex((spec) => spec.id === specId);
    if (specIndex === -1) return;

    const spec = specs[specIndex];
    const specValidation = validateSpecFields(spec, false);
    if (specValidation) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: specValidation,
      });
      return;
    }

    const variantIndex = spec.variants.findIndex((variant) => variant.id === variantId);
    if (variantIndex === -1) return;

    const variant = spec.variants[variantIndex];
    const variantValidation = validateVariantFields(variant);
    if (variantValidation) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: variantValidation,
      });
      return;
    }

    const { skuId, payload } = buildSkuPayload({
      specIndex,
      variantIndex,
      specName: spec.name.trim(),
      specValue: spec.value.trim(),
      variantName: variant.name.trim(),
      variantValue: variant.value.trim(),
      imageUrl: variant.imageUrl?.trim() || spec.imageUrl?.trim() || '',
      price: variant.price,
      quantity: variant.quantity,
    });

    setSkuMap((prev) => ({
      ...prev,
      [skuId]: payload,
    }));

    setSpecs((prev) =>
      prev.map((entry) => {
        if (entry.id !== specId) {
          return entry;
        }
        return {
          ...entry,
          variants: entry.variants.map((item) =>
            item.id === variantId
              ? {
                  ...item,
                  confirmed: true,
                  skuId,
                }
              : item
          ),
        };
      })
    );

    setSnackbar({
      open: true,
      severity: 'success',
      message: t('messages.variantConverted'),
    });
  };

  const payloadToSend = useMemo(() => {
    const uploadedUrls = imageState.files
      .map((fileEntry) => fileEntry.uploadedUrl)
      .filter(Boolean);
    const variants = Object.entries(skuMap).map(([skuId, data]) => ({
      sku_id: skuId,
      properties: data.properties,
      properties_name: data.properties_name,
      name: data.name,
      value: data.value,
      price: data.price,
      quantity: data.quantity,
      image_url: data.image_url,
    }));

    return {
      title: formState.title.trim(),
      description: formState.description.trim(),
      category_id: formState.categoryId || null,
      price: formState.price,
      images: uploadedUrls,
      description_images: uploadedUrls.slice(1),
      moq: formState.moq,
      detail_url: formState.detailUrl.trim() || 'https://manual-entry.checkstall/item',
      supplier_name: formState.supplierName.trim(),
      supplier_phone: formState.supplierPhone.trim(),
      supplier_wechat: formState.supplierWechat.trim(),
      supplier_note: formState.supplierNote.trim(),
      stock: formState.stock === '' ? null : Number(formState.stock),
      weight: formState.weight === '' ? null : formState.weight,
      volume: formState.volume === '' ? null : formState.volume,
      variants,
    };
  }, [formState, imageState.files, skuMap]);

  const validateForm = () => {
    const nextErrors = {};

    if (!formState.title.trim()) {
      nextErrors.title = t('validation.titleRequired');
    }
    if (!formState.description.trim()) {
      nextErrors.description = t('validation.descriptionRequired');
    }
    if (!formState.categoryId) {
      nextErrors.categoryId = t('validation.categoryRequired');
    }
    if (!formState.price || Number(formState.price) <= 0) {
      nextErrors.price = t('validation.pricePositive');
    }
    if (!formState.moq || Number(formState.moq) <= 0) {
      nextErrors.moq = t('validation.moqPositive');
    }
    if (!formState.supplierName.trim()) {
      nextErrors.supplierName = t('validation.supplierNameRequired');
    }
    if (imageState.uploading) {
      nextErrors.images = t('validation.imageUploadInProgress');
    } else if (!imageState.files.some((fileEntry) => fileEntry.uploadedUrl)) {
      nextErrors.images = t('validation.imageRequired');
    }
    if (formState.stock !== '' && Number(formState.stock) < 0) {
      nextErrors.stock = t('validation.stockNonNegative');
    }
    if (formState.weight !== '' && Number(formState.weight) < 0) {
      nextErrors.weight = t('validation.weightNonNegative');
    }
    if (formState.volume !== '' && Number(formState.volume) < 0) {
      nextErrors.volume = t('validation.volumeNonNegative');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: t('submitResolveIssues'),
      });
      return;
    }

    setSubmitting(true);
    setServerResponse(null);
    setPreviewPayload(payloadToSend);

    try {
      const response = await addLocalSupplierItem(payloadToSend);
      setServerResponse(response);
      setSnackbar({
        open: true,
        severity: 'success',
        message: response?.message || t('createdSuccess'),
      });
    } catch (error) {
      const message =
        error?.response?.data?.error?.detail ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        t('failedToCreateItem');

      setSnackbar({
        open: true,
        severity: 'error',
        message: typeof message === 'string' ? message : t('failedToCreateItem'),
      });

      if (error?.response?.data?.error && typeof error.response.data.error === 'object') {
        setErrors((prev) => ({
          ...prev,
          ...error.response.data.error,
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const skuRows = useMemo(
    () =>
      Object.entries(skuMap).map(([skuId, data]) => ({
        skuId,
        properties: data.properties_name,
        qty: data.quantity,
        price: data.price,
        total: (Number(data.price) || 0) * (Number(data.quantity) || 0),
      })),
    [skuMap]
  );

  const skuTotals = useMemo(
    () =>
      skuRows.reduce(
        (acc, row) => ({
          qty: acc.qty + (Number(row.qty) || 0),
          total: acc.total + (Number(row.total) || 0),
        }),
        { qty: 0, total: 0 }
      ),
    [skuRows]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t('title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('description')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RestartAlt />}
              onClick={resetForm}
            >
              {t('reset')}
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? t('creating') : t('createItem')}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t('sections.itemDetails')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={t('form.title')}
                      fullWidth
                      value={formState.title}
                      onChange={handleInputChange('title')}
                      error={Boolean(errors.title)}
                      helperText={errors.title}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={t('form.description')}
                      fullWidth
                      multiline
                      minRows={3}
                      value={formState.description}
                      onChange={handleInputChange('description')}
                      error={Boolean(errors.description)}
                      helperText={errors.description}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      label={t('form.category')}
                      fullWidth
                      value={formState.categoryId}
                      onChange={handleInputChange('categoryId')}
                      error={Boolean(errors.categoryId)}
                      helperText={errors.categoryId || categoryError}
                      disabled={loadingCategories}
                    >
                      {loadingCategories ? (
                        <MenuItem value="">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} />
                            <span>{t('loadingCategories')}</span>
                          </Stack>
                        </MenuItem>
                      ) : (
                        categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.price')}
                      fullWidth
                      value={formState.price}
                      onChange={handleInputChange('price')}
                      error={Boolean(errors.price)}
                      helperText={errors.price}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                        inputMode: 'decimal',
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.moq')}
                      fullWidth
                      value={formState.moq}
                      onChange={handleInputChange('moq')}
                      error={Boolean(errors.moq)}
                      helperText={errors.moq}
                      InputProps={{
                        inputMode: 'numeric',
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={t('form.detailUrl')}
                      fullWidth
                      value={formState.detailUrl}
                      onChange={handleInputChange('detailUrl')}
                      helperText={t('form.detailUrlHelp')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.stock')}
                      fullWidth
                      value={formState.stock}
                      onChange={handleInputChange('stock')}
                      error={Boolean(errors.stock)}
                      helperText={errors.stock || t('optional')}
                      InputProps={{
                        inputMode: 'numeric',
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.weight')}
                      fullWidth
                      value={formState.weight}
                      onChange={handleInputChange('weight')}
                      error={Boolean(errors.weight)}
                      helperText={errors.weight || t('optional')}
                      InputProps={{
                        inputMode: 'decimal',
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.volume')}
                      fullWidth
                      value={formState.volume}
                      onChange={handleInputChange('volume')}
                      error={Boolean(errors.volume)}
                      helperText={errors.volume || t('optional')}
                      InputProps={{
                        inputMode: 'decimal',
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <Divider />
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t('sections.supplierDetails')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.supplierName')}
                      fullWidth
                      value={formState.supplierName}
                      onChange={handleInputChange('supplierName')}
                      error={Boolean(errors.supplierName)}
                      helperText={errors.supplierName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.supplierPhone')}
                      fullWidth
                      value={formState.supplierPhone}
                      onChange={handleInputChange('supplierPhone')}
                      helperText={t('optional')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('form.supplierWechat')}
                      fullWidth
                      value={formState.supplierWechat}
                      onChange={handleInputChange('supplierWechat')}
                      helperText={t('optional')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={t('form.supplierNote')}
                      fullWidth
                      multiline
                      minRows={3}
                      value={formState.supplierNote}
                      onChange={handleInputChange('supplierNote')}
                      helperText={t('optional')}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <Divider />
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t('sections.itemImages')}
                </Typography>
                <Paper
                  elevation={imageState.dragActive ? 8 : 2}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: imageState.dragActive ? '2px dashed #4CAF50' : '2px dashed #ccc',
                    backgroundColor: imageState.dragActive ? '#E8F5E8' : 'transparent',
                    transition: 'all 0.3s ease',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: '#E8F5E8',
                      borderColor: '#4CAF50',
                    },
                  }}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept={acceptedFileTypes.join(',')}
                    multiple
                    onChange={handleImageChange}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {imageState.uploading ? t('form.uploading') : t('form.dragDropOrClick')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('form.fileTypes', { maxSize: Math.round(maxFileSize / (1024 * 1024)) })}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                    {t('form.imagesHelp')}
                  </Typography>
                </Paper>
                {imageState.uploading && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress />
                  </Box>
                )}
                {!!imageState.files.length && (
                  <Grid container spacing={2} mt={0.5}>
                    {imageState.files.map((fileEntry, index) => (
                      <Grid key={fileEntry.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Box
                            component="img"
                            src={fileEntry.preview}
                            alt={t('imageAlt')}
                            sx={{
                              width: '100%',
                              height: 180,
                              objectFit: 'cover',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'background.default',
                              mb: 1.5,
                            }}
                          />
                          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <Chip
                              size="small"
                              color={index === 0 ? 'primary' : 'success'}
                              label={index === 0 ? t('mainImage') : t('imageLabel', { number: index + 1 })}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={
                                fileEntry.deleting
                                  ? t('form.deleting')
                                  : fileEntry.uploadedUrl
                                    ? t('form.uploaded')
                                    : t('form.pending')
                              }
                              color={
                                fileEntry.deleting
                                  ? 'default'
                                  : fileEntry.uploadedUrl
                                    ? 'success'
                                    : 'warning'
                              }
                            />
                            <Box flexGrow={1} />
                            <IconButton
                              size="small"
                              disabled={fileEntry.deleting}
                              onClick={() => handleRemoveImage(fileEntry.id)}
                            >
                              {fileEntry.deleting ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Stack>
                          <Typography variant="body2" noWrap title={fileEntry.name}>
                            {fileEntry.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(fileEntry.size)}
                          </Typography>
                          {fileEntry.uploadedUrl && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 1, wordBreak: 'break-all' }}
                            >
                              {fileEntry.uploadedUrl}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {errors.images && (
                  <Typography variant="caption" color="error" display="block" mt={1}>
                    {errors.images}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Box mt={3}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      {t('sections.specificationsVariants')}
                    </Typography>
                    <Button startIcon={<AddCircleOutline />} variant="contained" onClick={handleAddSpec}>
                      {t('specifications.addSpecification')}
                    </Button>
                  </Stack>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('specifications.help')}
                  </Alert>
                  {!specs.length && (
                    <Typography variant="body2" color="text.secondary">
                      {t('specifications.empty')}
                    </Typography>
                  )}
                  <Stack spacing={2}>
                    {specs.map((spec, specIdx) => (
                      <Card key={spec.id} variant="outlined" sx={{ borderColor: spec.confirmed ? 'success.light' : 'divider' }}>
                        <CardContent>
                          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {t('specifications.specification', { number: specIdx + 1 })}
                            </Typography>
                            {spec.confirmed && (
                              <Chip
                                size="small"
                                icon={<CheckCircle fontSize="small" />}
                                label={t('specifications.skuReady')}
                                color="success"
                                variant="outlined"
                              />
                            )}
                            {!!spec.skuId && (
                              <Chip size="small" label={t('specifications.skuChip', { id: spec.skuId })} variant="outlined" sx={{ ml: 1 }} />
                            )}
                            <Box flexGrow={1} />
                            <Tooltip title={t('specifications.removeSpecification')}>
                              <IconButton color="error" onClick={() => handleRemoveSpec(spec.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                label={t('specifications.name')}
                                fullWidth
                                value={spec.name}
                                onChange={(event) => updateSpecField(spec.id, 'name', event.target.value)}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                label={t('specifications.value')}
                                fullWidth
                                value={spec.value}
                                onChange={(event) => updateSpecField(spec.id, 'value', event.target.value)}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                label={t('specifications.price')}
                                fullWidth
                                value={spec.price}
                                onChange={(event) => updateSpecField(spec.id, 'price', event.target.value)}
                                InputProps={{ inputMode: 'decimal' }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                label={t('specifications.quantity')}
                                fullWidth
                                value={spec.quantity}
                                onChange={(event) => updateSpecField(spec.id, 'quantity', event.target.value)}
                                InputProps={{ inputMode: 'numeric' }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                label={t('specifications.imageUrl', { defaultValue: 'Specification image URL' })}
                                fullWidth
                                value={spec.imageUrl}
                                onChange={(event) => updateSpecField(spec.id, 'imageUrl', event.target.value)}
                                helperText={t('specifications.imageUrlHelp', { defaultValue: 'Optional. Upload the image above, then paste its URL here to show this option image in the app.' })}
                              />
                            </Grid>
                          </Grid>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddVariant(spec.id)}
                            >
                              {t('specifications.addVariant')}
                            </Button>
                            <Tooltip title={t('specifications.specTooltip')}>
                              <Button
                                variant="outlined"
                                color="success"
                                startIcon={<CheckCircle />}
                                onClick={() => handleGenerateSpecSku(spec.id)}
                                disabled={spec.variants && spec.variants.length > 0}
                              >
                                {t('specifications.generateFromSpec')}
                              </Button>
                            </Tooltip>
                          </Stack>
                        </CardContent>
                        {!!spec.variants.length && (
                          <>
                            <Divider />
                            <CardContent>
                              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {t('specifications.variants')}
                                </Typography>
                                <Tooltip title={t('specifications.variantTooltip')}>
                                  <InfoOutlined fontSize="small" color="action" />
                                </Tooltip>
                              </Stack>
                              <Stack spacing={2}>
                                {spec.variants.map((variant, variantIdx) => (
                                  <Card
                                    key={variant.id}
                                    variant="outlined"
                                    sx={{ borderColor: variant.confirmed ? 'success.light' : 'divider' }}
                                  >
                                    <CardContent>
                                      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                          {t('specifications.variant', { number: variantIdx + 1 })}
                                        </Typography>
                                        {variant.confirmed && (
                                          <Chip
                                            size="small"
                                            icon={<CheckCircle fontSize="small" />}
                                            label={t('specifications.skuReady')}
                                            color="success"
                                            variant="outlined"
                                          />
                                        )}
                                        {!!variant.skuId && (
                                          <Chip size="small" label={t('specifications.skuChip', { id: variant.skuId })} variant="outlined" sx={{ ml: 1 }} />
                                        )}
                                        <Box flexGrow={1} />
                                        <Tooltip title={t('specifications.removeVariant')}>
                                          <IconButton
                                            color="error"
                                            onClick={() => handleRemoveVariant(spec.id, variant.id)}
                                          >
                                            <CancelIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                      <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                          <TextField
                                            label={t('specifications.variantName')}
                                            fullWidth
                                            value={variant.name}
                                            onChange={(event) =>
                                              updateVariantField(spec.id, variant.id, 'name', event.target.value)
                                            }
                                          />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                          <TextField
                                            label={t('specifications.variantValue')}
                                            fullWidth
                                            value={variant.value}
                                            onChange={(event) =>
                                              updateVariantField(spec.id, variant.id, 'value', event.target.value)
                                            }
                                          />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                          <TextField
                                            label={t('specifications.price')}
                                            fullWidth
                                            value={variant.price}
                                            onChange={(event) =>
                                              updateVariantField(spec.id, variant.id, 'price', event.target.value)
                                            }
                                            InputProps={{ inputMode: 'decimal' }}
                                          />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                          <TextField
                                            label={t('specifications.quantity')}
                                            fullWidth
                                            value={variant.quantity}
                                            onChange={(event) =>
                                              updateVariantField(spec.id, variant.id, 'quantity', event.target.value)
                                            }
                                            InputProps={{ inputMode: 'numeric' }}
                                          />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                          <TextField
                                            label={t('specifications.variantImageUrl', { defaultValue: 'Variant image URL' })}
                                            fullWidth
                                            value={variant.imageUrl}
                                            onChange={(event) =>
                                              updateVariantField(spec.id, variant.id, 'imageUrl', event.target.value)
                                            }
                                            helperText={t('specifications.imageUrlHelp', { defaultValue: 'Optional. Upload the image above, then paste its URL here to show this option image in the app.' })}
                                          />
                                        </Grid>
                                      </Grid>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                      <Button
                                        variant="outlined"
                                        color="success"
                                        startIcon={<CheckCircle />}
                                        onClick={() => handleGenerateVariantSku(spec.id, variant.id)}
                                      >
                                        {t('specifications.generateFromVariant')}
                                      </Button>
                                    </CardActions>
                                  </Card>
                                ))}
                              </Stack>
                            </CardContent>
                          </>
                        )}
                      </Card>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      {t('sections.generatedSkus', { count: skuRows.length })}
                    </Typography>
                    <Tooltip title={t('specifications.variantTooltip')}>
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                  {!skuRows.length ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('messages.noSkus')}
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('table.skuId')}</TableCell>
                            <TableCell>{t('table.properties')}</TableCell>
                            <TableCell align="right">{t('table.qty')}</TableCell>
                            <TableCell align="right">{t('table.price')}</TableCell>
                            <TableCell align="right">{t('table.total')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {skuRows.map((row) => (
                            <TableRow key={row.skuId}>
                              <TableCell>{row.skuId}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{row.properties}</Typography>
                              </TableCell>
                              <TableCell align="right">{row.qty}</TableCell>
                              <TableCell align="right">¥{row.price?.toLocaleString?.() || row.price}</TableCell>
                              <TableCell align="right">¥{row.total?.toLocaleString?.() || row.total}</TableCell>
                            </TableRow>
                          ))}
                          {!!skuRows.length && (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                                {t('table.summary')}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {skuTotals.qty.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">-</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                ¥{skuTotals.total.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      {t('sections.payloadPreview')}
                    </Typography>
                    <Tooltip title={t('payloadPreview.tooltip')}>
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                  <Box
                    component="pre"
                    sx={{
                      maxHeight: 360,
                      overflow: 'auto',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'grey.900',
                      color: 'grey.100',
                      fontSize: 12,
                    }}
                  >
                    {JSON.stringify(previewPayload || payloadToSend, null, 2)}
                  </Box>
                </CardContent>
              </Card>

              {serverResponse && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {t('sections.serverResponse')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {serverResponse?.message || t('createdSuccess')}
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>{t('serverResponse.itemNumber')}</TableCell>
                          <TableCell>{serverResponse?.item?.item_number}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{t('serverResponse.source')}</TableCell>
                          <TableCell>{serverResponse?.item?.source}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{t('serverResponse.title')}</TableCell>
                          <TableCell>{serverResponse?.item?.title}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{t('serverResponse.supplier')}</TableCell>
                          <TableCell>{serverResponse?.item?.supplier_name}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default LocalSupplierItemsPage;
